/**
 * One line for a per-level track (issue 628): the compare view cannot fit a
 * class's twenty-row table in a cell, but it can say what the track amounts
 * to, and the same rule serves any Family that declares one — the registry
 * says only `structured`, so the shape is read from the value:
 *
 *   a list of numbers          → "+0 to +12 over 20 levels"
 *                                (a save or an attack progression; the ends
 *                                 say good or poor without a d20 word for it)
 *   a list of lists of numbers → "spells to 9th level, over 20 levels"
 *                                (a slots grid: the highest column with any
 *                                 slot on the last row)
 *
 * Anything else is not a track and answers null, which the compare view
 * renders as no row — a structured value that is not per-level stays out, as
 * every structured value did before.
 */
import { formatSigned } from './displayFormula';

const isNumbers = (v) => Array.isArray(v) && v.length > 0 && v.every((n) => typeof n === 'number' && Number.isFinite(n));
// A spells-per-day grid: a blank ("—") cell is null (qa-runs 390).
const isGridRow = (r) => Array.isArray(r) && r.length > 0 && r.every((n) => n === null || (typeof n === 'number' && Number.isFinite(n)))
  && r.some((n) => n !== null);
const isGrid = (v) => Array.isArray(v) && v.length > 0 && v.every((r) => Array.isArray(r) && r.length > 0
  && r.every((n) => n === null || (typeof n === 'number' && Number.isFinite(n)))) && v.some(isGridRow);

const ORDINAL = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const r = n % 100;
  return `${n}${s[(r - 20) % 10] || s[r] || s[0]}`;
};

export function summarizeTrack(value) {
  if (isNumbers(value)) {
    const first = value[0];
    const last = value[value.length - 1];
    return `${formatSigned(first)} to ${formatSigned(last)} over ${value.length} levels`;
  }
  if (isGrid(value)) {
    const lastRow = value[value.length - 1];
    let top = -1;
    lastRow.forEach((n, i) => { if (typeof n === 'number' && n > 0) top = i; });
    if (top < 0) return `no spells over ${value.length} levels`;
    return `spells to ${ORDINAL(top)} level, over ${value.length} levels`;
  }
  return null;
}

export default summarizeTrack;
